/**
 * Release 3.3 item C2: what the risk-reduction card says and draws, as pure
 * functions of the server's series. Nothing here invents a number: a day with
 * no snapshot is a gap (null), a tenant with no history is an empty state, and
 * history shorter than the chosen range says when it starts.
 */
import type { RiskSeries, RiskSeriesPoint } from '@/lib/api';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** 'YYYY-MM-DD' -> '10 Mar'; with the year -> '10 Mar 2026'. No Date object, so no time zone. */
export function formatDay(day: string, withYear = false): string {
  const [y, m, d] = day.split('-');
  const month = MONTHS[Number(m) - 1] ?? m;
  return `${Number(d)} ${month}${withYear ? ` ${y}` : ''}`;
}

export interface RiskChartRow {
  /** Axis label, e.g. '10 Mar'. */
  label: string;
  day: string;
  open: number | null;
  resolved: number | null;
  opened: number | null;
  riskScoreTotal: number | null;
}

export function chartRows(points: ReadonlyArray<RiskSeriesPoint>): RiskChartRow[] {
  return points.map((p) => ({
    label: formatDay(p.day),
    day: p.day,
    open: p.open,
    resolved: p.resolved,
    opened: p.opened,
    riskScoreTotal: p.riskScoreTotal,
  }));
}

export type RiskSeriesView =
  | { state: 'empty'; message: string }
  | {
      state: 'ready';
      /** One sentence on the direction of travel; null with a single recorded day. */
      headline: string | null;
      /** Shown when history is shorter than the range asked for. */
      historyNote: string | null;
      /** Days inside the drawn window with no snapshot. */
      gapDays: number;
      rows: RiskChartRow[];
    };

const move = (n: number, noun: string): string =>
  n === 0 ? `${noun} unchanged` : `${noun} ${n < 0 ? 'down' : 'up'} ${Math.abs(n).toLocaleString('en-IN')}`;

export function describeSeries(series: RiskSeries): RiskSeriesView {
  if (series.points.length === 0 || series.historyStartsOn === null) {
    return {
      state: 'empty',
      message:
        'No history yet. The daily snapshot has not recorded a day for the business units you can see. ' +
        'The first point appears after the next run; nothing is drawn until then.',
    };
  }
  const recorded = series.points.filter((p) => p.recorded);
  const s = series.summary;
  let headline: string | null = null;
  if (s) {
    const first = recorded[0];
    const last = recorded[recorded.length - 1];
    const parts = [`${move(s.openChange, 'Unresolved findings')} since ${formatDay(s.from)}`];
    if (first?.open != null && last?.open != null) parts[0] += ` (${first.open.toLocaleString('en-IN')} to ${last.open.toLocaleString('en-IN')})`;
    parts.push(`${s.resolvedTotal.toLocaleString('en-IN')} resolved, ${s.openedTotal.toLocaleString('en-IN')} newly opened`);
    if (s.riskScoreChange !== null && first?.riskScoreTotal != null && last?.riskScoreTotal != null) {
      parts.push(`risk score ${first.riskScoreTotal.toLocaleString('en-IN')} to ${last.riskScoreTotal.toLocaleString('en-IN')}`);
    }
    headline = `${parts.join('; ')}.`;
  }
  // The window the server drew starts at max(range start, history start); a
  // window shorter than the range means history is what ran out.
  const shorter = series.points.length < series.range;
  return {
    state: 'ready',
    headline,
    historyNote: shorter
      ? `History starts on ${formatDay(series.historyStartsOn, true)}. Nothing was recorded before that day and past days cannot be rebuilt, so none are drawn as zeros.`
      : null,
    gapDays: series.points.length - recorded.length,
    rows: chartRows(series.points),
  };
}
